import apiService from 'src/shared/Services/apiService';
import {
  COOKIE_CATEGORIES,
  CLIENT_STORAGE_POLICY_VERSION,
  COOKIE_CONSENT_MAX_AGE_DAYS,
  KEYS_CLEARED_ON_LOGOUT,
  getItemsForCategory,
  type CookieCategory,
} from './cookieRegistry';

/**
 * Cookie consent store. The banner runs pre-sign-in, so records are keyed on a
 * client-minted anonymous id against an UNAUTHENTICATED endpoint, then linked to
 * the account on sign-in. localStorage is the fast path; the account copy makes
 * the choice portable across devices.
 */

/**
 * Fired after the account's cookie consent is pulled down and applied locally,
 * so a mounted banner can re-read rather than sit on stale state.
 */
export const COOKIE_CONSENT_UPDATED_EVENT = 'cookieConsentUpdated';

const CONSENT_KEY = 'cookieConsent';
const ANON_ID_KEY = 'symbian_anon_id';
const RETRY_AT_KEY = 'symbian_cookie_retry_after';
/** Gap between retries — the endpoint is rate limited per IP. */
const RETRY_BACKOFF_MS = 5 * 60 * 1000;
/** Longer pause after a 429, so a shared IP recovers instead of being re-hit. */
const RATE_LIMIT_COOLDOWN_MS = 30 * 60 * 1000;

export interface CookiePreferences {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface StoredCookieConsent extends CookiePreferences {
  /** Policy version agreed to — a bump re-prompts. */
  version: string;
  timestamp: string;
  anonymousId: string;
  /** True once the backend has acknowledged the write. */
  synced: boolean;
  /** Made by a signed-in account, so it must not outlive their session. */
  accountLinked?: boolean;
}

export const DEFAULT_PREFERENCES: CookiePreferences = {
  essential: true,   // cannot be refused
  functional: false, // everything else defaults OFF — no pre-ticked boxes
  analytics: false,
  marketing: false,
};

const safeGet = (k: string): string | null => {
  try { return localStorage.getItem(k); } catch { return null; }
};
const safeSet = (k: string, v: string): void => {
  try { localStorage.setItem(k, v); } catch { /* private mode */ }
};
const safeRemove = (k: string): void => {
  try { localStorage.removeItem(k); } catch { /* ignore */ }
};

/** Stable per-browser id so an anonymous consent can be linked on sign-in. */
export const getAnonymousId = (): string => {
  const existing = safeGet(ANON_ID_KEY);
  if (existing) return existing;

  const id = globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : `anon-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  safeSet(ANON_ID_KEY, id);
  return id;
};

export const readCookieConsent = (): StoredCookieConsent | null => {
  const raw = safeGet(CONSENT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredCookieConsent;
  } catch {
    // Corrupt or legacy shape — treat as no consent and re-ask.
    return null;
  }
};

/**
 * Do we need to show the banner?
 *
 * Yes when: never asked, the policy version changed, or the consent is older
 * than a year. Annual re-consent is the widely accepted reading of "consent
 * must remain informed"; a bump covers material changes in between.
 */
export const needsCookieConsent = (): boolean => {
  const stored = readCookieConsent();
  if (!stored) return true;
  if (stored.version !== CLIENT_STORAGE_POLICY_VERSION) return true;

  const age = Date.now() - new Date(stored.timestamp).getTime();
  if (Number.isNaN(age)) return true;
  return age > COOKIE_CONSENT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
};

export const getEffectivePreferences = (): CookiePreferences => {
  const stored = readCookieConsent();
  if (!stored || needsCookieConsent()) return DEFAULT_PREFERENCES;
  return {
    essential: true,
    functional: !!stored.functional,
    analytics: !!stored.analytics,
    marketing: !!stored.marketing,
  };
};

/** Is a category currently permitted? Gate every non-essential script on this. */
export const isCategoryAllowed = (category: CookieCategory): boolean => {
  if (COOKIE_CATEGORIES[category].alwaysOn) return true;
  return getEffectivePreferences()[category] === true;
};

/**
 * Persist a choice: local first (so it takes effect immediately), then the
 * server. A failed sync is retried on next load rather than lost.
 */
/** Identical prefs? `essential` excluded — always true. */
const samePreferences = (a: CookiePreferences, b: CookiePreferences): boolean =>
  a.functional === b.functional
  && a.analytics === b.analytics
  && a.marketing === b.marketing;

// Collapses double-clicks into one request — the endpoint is rate limited per IP.
let inFlight: Promise<StoredCookieConsent> | null = null;

export const saveCookieConsent = async (
  prefs: CookiePreferences,
): Promise<StoredCookieConsent> => {
  const existing = readCookieConsent();

  // Nothing changed and the server has it — don't spend a rate-limited request.
  if (
    existing
    && existing.synced
    && existing.version === CLIENT_STORAGE_POLICY_VERSION
    && samePreferences(existing, prefs)
  ) {
    return existing;
  }

  if (inFlight) return inFlight;

  // Rate limited recently: keep the choice locally and send it when the
  // cooldown lapses, so a change made during it is not stranded until a reload.
  if (isRateLimited()) {
    const record = storeLocallyOnly(prefs);
    scheduleCooldownFlush(Number(safeGet(RETRY_AT_KEY) ?? 0) - Date.now());
    return record;
  }

  inFlight = saveCookieConsentInner(prefs).finally(() => { inFlight = null; });
  return inFlight;
};

/** Is the cookie-consent API cooling down after a 429? */
const isRateLimited = (): boolean => {
  const until = Number(safeGet(RETRY_AT_KEY) ?? 0);
  return Number.isFinite(until) && Date.now() < until;
};

/** Start the shared cooldown covering POST, GET and link. */
const noteRateLimited = (): void => {
  safeSet(RETRY_AT_KEY, String(Date.now() + RATE_LIMIT_COOLDOWN_MS));
};

let cooldownTimer: ReturnType<typeof setTimeout> | null = null;

/** Flushes the pending choice once the cooldown lapses, without needing a reload. */
const scheduleCooldownFlush = (delayMs: number): void => {
  if (cooldownTimer) return;
  cooldownTimer = setTimeout(() => {
    cooldownTimer = null;
    retryCookieConsentSync();
  }, Math.max(0, delayMs) + 1000);
};

/** Writes the choice locally and leaves it unsynced for the retry to pick up. */
const storeLocallyOnly = (prefs: CookiePreferences): StoredCookieConsent => {
  const record: StoredCookieConsent = {
    ...prefs,
    essential: true,
    version: CLIENT_STORAGE_POLICY_VERSION,
    timestamp: new Date().toISOString(),
    anonymousId: getAnonymousId(),
    synced: false,
  };
  safeSet(CONSENT_KEY, JSON.stringify(record));
  purgeDisallowedStorage(record);
  return record;
};

const saveCookieConsentInner = async (
  prefs: CookiePreferences,
): Promise<StoredCookieConsent> => {
  const record: StoredCookieConsent = {
    ...prefs,
    essential: true,
    version: CLIENT_STORAGE_POLICY_VERSION,
    timestamp: new Date().toISOString(),
    anonymousId: getAnonymousId(),
    synced: false,
  };

  safeSet(CONSENT_KEY, JSON.stringify(record));
  purgeDisallowedStorage(record);

  try {
    await apiService.recordCookieConsent({
      anonymousId: record.anonymousId,
      policyVersion: record.version,
      preferences: {
        essential: true,
        functional: record.functional,
        analytics: record.analytics,
        marketing: record.marketing,
      },
      clientRecordedAt: record.timestamp,
    });
    record.synced = true;
    safeSet(CONSENT_KEY, JSON.stringify(record));

    linkAnonymousConsent().then((linked) => {
      if (!linked) return;
      const current = readCookieConsent();
      if (!current) return;
      safeSet(CONSENT_KEY, JSON.stringify({ ...current, accountLinked: true }));
    });
  } catch (err: any) {
    const status = err?.response?.status;

    // Non-429 4xx is permanent: mark settled so we stop retrying forever.
    if (status && status >= 400 && status < 500 && status !== 429) {
      record.synced = true;
      safeSet(CONSENT_KEY, JSON.stringify(record));
    } else {
      // 429 gets a longer cooldown — the endpoint is rate limited per IP, so a
      // whole clinic behind one address must back off together, not retry harder.
      const wait = status === 429 ? RATE_LIMIT_COOLDOWN_MS : RETRY_BACKOFF_MS;
      safeSet(RETRY_AT_KEY, String(Date.now() + wait));
    }
  }

  return record;
};

/** Re-send an unsynced consent on app start, backed off so a clinic's shared IP isn't drained. */
export const retryCookieConsentSync = async (): Promise<void> => {
  const stored = readCookieConsent();
  if (!stored || stored.synced) return;

  const retryAfter = Number(safeGet(RETRY_AT_KEY) ?? 0);
  if (Number.isFinite(retryAfter) && Date.now() < retryAfter) return;

  try {
    await apiService.recordCookieConsent({
      anonymousId: stored.anonymousId,
      policyVersion: stored.version,
      preferences: {
        essential: true,
        functional: stored.functional,
        analytics: stored.analytics,
        marketing: stored.marketing,
      },
      clientRecordedAt: stored.timestamp,
    });
    safeSet(CONSENT_KEY, JSON.stringify({ ...stored, synced: true }));
    safeRemove(RETRY_AT_KEY);
  } catch (err: any) {
    const status = err?.response?.status;

    // Permanent rejection — stop retrying.
    if (status && status >= 400 && status < 500 && status !== 429) {
      safeSet(CONSENT_KEY, JSON.stringify({ ...stored, synced: true }));
      safeRemove(RETRY_AT_KEY);
      return;
    }

    // 429 gets the longer shared cooldown; other transients a short backoff.
    safeSet(RETRY_AT_KEY, String(Date.now() + (status === 429 ? RATE_LIMIT_COOLDOWN_MS : RETRY_BACKOFF_MS)));
  }
};
export const linkAnonymousConsent = async (): Promise<boolean> => {
  const stored = readCookieConsent();
  if (!stored?.anonymousId) return false;
  if (isRateLimited()) return false;
  try {
    await apiService.linkAnonymousCookieConsent(stored.anonymousId);
    return true;
  } catch (err: any) {
    if (err?.response?.status === 429) noteRateLimited();
    // 404 is expected and non-fatal: no server-side record to attach yet.
    return false;
  }
};
export const syncCookieConsentFromAccount = async (): Promise<void> => {
  // Rate limited recently — the local copy already governs, so skip the round
  // trip entirely rather than spending a request that will be refused.
  if (isRateLimited()) return;

  const local = readCookieConsent();

  // Only link a record the server actually has. Linking an unsynced one just
  // earns a 404, because the POST that would have created it never succeeded.
  if (local?.synced) await linkAnonymousConsent();

  let remote;
  try {
    const state = await apiService.getCookieConsent();
    remote = state?.current;
  } catch (err: any) {
    if (err?.response?.status === 429) noteRateLimited();
    return; // Offline, rate limited or not yet built — local copy stands.
  }
  if (!remote?.recordedAt) return;

  const remoteTime = new Date(remote.recordedAt).getTime();
  const localTime = local ? new Date(local.timestamp).getTime() : -Infinity;

  if (Number.isNaN(remoteTime)) return;
  if (local && !Number.isNaN(localTime) && localTime >= remoteTime) return;

  const applied: StoredCookieConsent = {
    essential: true,
    functional: !!remote.preferences?.functional,
    analytics: !!remote.preferences?.analytics,
    marketing: !!remote.preferences?.marketing,
    version: remote.policyVersion,
    timestamp: remote.recordedAt,
    anonymousId: local?.anonymousId ?? getAnonymousId(),
    synced: true,
    accountLinked: true,
  };

  safeSet(CONSENT_KEY, JSON.stringify(applied));
  purgeDisallowedStorage(applied);

  try {
    window.dispatchEvent(new Event(COOKIE_CONSENT_UPDATED_EVENT));
  } catch {

   }
};

export const purgeDisallowedStorage = (prefs: CookiePreferences): void => {
  (Object.keys(COOKIE_CATEGORIES) as CookieCategory[]).forEach((category) => {
    if (COOKIE_CATEGORIES[category].alwaysOn) return;
    if (prefs[category]) return;

    // Remove every stored item belonging to a now-refused category.
    getItemsForCategory(category)
      .filter((i) => !i.key.includes('*'))
      .forEach((i) => safeRemove(i.key));
  });
};
export const clearSessionScopedStorage = (): void => {
  KEYS_CLEARED_ON_LOGOUT.forEach(safeRemove);
  safeRemove('stepper_completed');

  // redux-persist and Cognito use prefixed key families.
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith('persist:') || k.startsWith('CognitoIdentityServiceProvider.'))
      .forEach(safeRemove);
  } catch { /* ignore */ }

  clearAccountLinkedCookieConsent();
};
export const clearAccountLinkedCookieConsent = (): void => {
  const stored = readCookieConsent();
  if (!stored?.accountLinked) return;

  safeRemove(CONSENT_KEY);
  purgeDisallowedStorage(DEFAULT_PREFERENCES);

  try {
    window.dispatchEvent(new Event(COOKIE_CONSENT_UPDATED_EVENT));
  } catch { /* non-browser context */ }
};
